import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { Avatar, Box, Button, IconButton, Typography } from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import GetAppIcon from '@material-ui/icons/GetApp';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import TableChartIcon from '@material-ui/icons/TableChart';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import * as XLSX from 'xlsx';
import TypingAnimation from 'src/components/animate/TypingAnimation';
import moment from 'moment';
import { WA } from '../whatsappTheme';
import defaultAvatar from '../../../../assets/defaultAvatar.png';
import { START_CHAT_MESSAGE, parsePendingJoinMessage } from '../helpDeskSession'
import { ChatRatingBadge } from '../ChatFeedbackPanel'
import { splitReplyHtml } from '../chatReply'
import ReplyIcon from '@material-ui/icons/Reply'

const HELP_DESK_USER_ID = 39

export const getMessageReceiptStatus = (message, recipientOnline = false, isGroupChat = false) => {
  if (!message || message.isTyping || message.contentType === 'bot') return null
  if (message.contentType === 'system' || message.contentType === 'end') return null
  if (isGroupChat) {
    if (Number(message.read_status) === 0) return 'read'
    return 'delivered'
  }
  // One-way: read > delivered > sent (never downgrade)
  if (Number(message.read_status) === 0) return 'read'
  if (message.delivered === true) return 'delivered'
  if (recipientOnline) return 'delivered'
  return 'sent'
}

function MessageTicks({ status }) {
  if (!status) return null
  const readColor = '#53BDEB'
  const defaultColor = WA.meta
  const color = status === 'read' ? readColor : defaultColor
  const Icon = status === 'sent' ? DoneIcon : DoneAllIcon

  return (
    <Icon
      sx={{
        width: 16,
        height: 16,
        color,
        ml: 0.25,
        flexShrink: 0,
      }}
    />
  )
}

const isPhotoUrl = (value) => {
  const url = String(value || '').trim()
  if (!url || url === 'avatar.jpg' || url === 'null' || url === 'undefined') return ''
  return url
}

const personPhoto = (name, participants, fallback) => {
  const people = Array.isArray(participants) ? participants : []
  const normalized = String(name || '').trim().toLowerCase()
  const match = people.find((person) => {
    const full = (
      person?.full_name ||
      [person?.first_name, person?.last_name].filter(Boolean).join(' ')
    )
      .trim()
      .toLowerCase()
    return full === normalized
  })
  return isPhotoUrl(match?.profile_pic) || isPhotoUrl(fallback)
}

const getLoggedInUser = () => {
  try {
    const raw = localStorage.getItem('user')
    if (!raw || raw === 'null' || raw === 'undefined') return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const isAddedUserViewer = (addedName, participants) => {
  const user = getLoggedInUser()
  if (!user?.user_id || !addedName) return false
  const normalizedAdded = String(addedName).trim().toLowerCase()
  const myDisplayName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
    .toLowerCase()
  if (myDisplayName && myDisplayName === normalizedAdded) return true
  const people = Array.isArray(participants) ? participants : []
  const addedPerson = people.find((person) => {
    const full = (
      person?.full_name ||
      [person?.first_name, person?.last_name].filter(Boolean).join(' ')
    )
      .trim()
      .toLowerCase()
    return full === normalizedAdded
  })
  return Boolean(addedPerson && Number(addedPerson.user_id) === Number(user.user_id))
}

const cleanAttachmentName = (name = '') => {
  const raw = String(name || '').trim()
  if (!raw) return 'Document'
  try {
    return decodeURIComponent(raw.replace(/^\d{10,}_/, '')) || raw
  } catch {
    return raw.replace(/^\d{10,}_/, '') || raw
  }
}

const getFileMeta = (fileName = '') => {
  const ext = (String(fileName).split('.').pop() || '').toLowerCase()
  if (ext === 'pdf') {
    return { label: 'PDF Document', color: '#E53935', Icon: PictureAsPdfIcon, kind: 'pdf' }
  }
  if (['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'mkv'].includes(ext)) {
    return { label: 'Video', color: '#5E35B1', Icon: InsertDriveFileIcon, kind: 'video' }
  }
  if (['mp3', 'wav', 'm4a', 'aac', 'flac'].includes(ext)) {
    return { label: 'Audio', color: '#00897B', Icon: InsertDriveFileIcon, kind: 'audio' }
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return { label: 'Excel Spreadsheet', color: '#43A047', Icon: TableChartIcon, kind: 'office' }
  }
  if (['doc', 'docx', 'rtf'].includes(ext)) {
    return { label: 'Document', color: '#1E88E5', Icon: DescriptionIcon, kind: 'office' }
  }
  if (['ppt', 'pptx'].includes(ext)) {
    return { label: 'Presentation', color: '#FB8C00', Icon: InsertDriveFileIcon, kind: 'office' }
  }
  if (['txt'].includes(ext)) {
    return { label: 'Document', color: '#1E88E5', Icon: DescriptionIcon, kind: 'text' }
  }
  if (['zip', 'rar', '7z'].includes(ext)) {
    return { label: 'Compressed file', color: '#8E24AA', Icon: InsertDriveFileIcon, kind: 'file' }
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { label: 'Image', color: '#039BE5', Icon: InsertDriveFileIcon, kind: 'image' }
  }
  return {
    label: ext ? `${ext.toUpperCase()} File` : 'Document',
    color: '#607D8B',
    Icon: InsertDriveFileIcon,
    kind: 'file',
  }
}

const getAttachmentKind = (url = '', fileName = '') => {
  const fromName = getFileMeta(fileName || url).kind
  if (fromName && fromName !== 'file') return fromName
  const src = String(url || '')
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(src)) return 'image'
  if (/\.(mp4|webm|ogg|mov|m4v|avi|mkv)(\?|$)/i.test(src)) return 'video'
  if (/\.(mp3|wav|m4a|aac|flac)(\?|$)/i.test(src)) return 'audio'
  if (/\.pdf(\?|$)/i.test(src)) return 'pdf'
  if (/\.(xlsx?|csv|docx?|pptx?|rtf)(\?|$)/i.test(src)) return 'office'
  if (/\.txt(\?|$)/i.test(src)) return 'text'
  return 'file'
}

const getInlinePreviewSrc = (kind, url = '') => {
  if (!url) return ''
  if (kind === 'office') {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
  }
  return url
}

const canShowPagePreview = (kind) =>
  kind === 'pdf' || kind === 'office' || kind === 'text'

const isSpreadsheetKind = (kind, fileName = '') => {
  if (kind !== 'office' && kind !== 'file') return false
  return /\.(xlsx?|csv)(\?|$)/i.test(String(fileName || ''))
}

function SpreadsheetPagePreview({ url, fileName }) {
  const [rows, setRows] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setFailed(false)
    if (!url) {
      setFailed(true)
      return undefined
    }
    ;(async () => {
      try {
        const response = await fetch(url)
        if (!response.ok) throw new Error('Failed to load spreadsheet')
        const buffer = await response.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]
        const data = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
          blankrows: false,
        })
        if (cancelled) return
        setRows((data || []).slice(0, 8).map((row) => (row || []).slice(0, 5)))
      } catch (error) {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [url])

  if (failed) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          bgcolor: '#fff',
        }}
      >
        <Typography variant="caption" sx={{ color: WA.meta, textAlign: 'center' }}>
          {fileName || 'Spreadsheet'}
        </Typography>
      </Box>
    )
  }

  if (!rows) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
        }}
      >
        <Typography variant="caption" sx={{ color: WA.meta }}>
          Loading preview…
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', overflow: 'hidden', bgcolor: '#fff', p: 0.75 }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontSize: 10,
          lineHeight: 1.25,
          color: WA.text,
          '& td': {
            border: `1px solid ${WA.border}`,
            px: 0.5,
            py: 0.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 0,
          },
          '& tr:first-of-type td': {
            fontWeight: 700,
            bgcolor: '#F4F6F8',
          },
        }}
      >
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`r-${rowIndex}`}>
              {(row.length ? row : ['']).map((cell, cellIndex) => (
                <td key={`c-${rowIndex}-${cellIndex}`} title={String(cell ?? '')}>
                  {String(cell ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  )
}

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(1.75),
  '&.chat-msg-highlight .chat-bubble-content': {
    boxShadow: `0 0 0 2px ${WA.header}`,
    transition: 'box-shadow 0.2s ease',
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 'min(360px, 100%)',
  minWidth: 0,
  width: '100%',
  padding: theme.spacing(1.15, 1.6),
  borderRadius: 18,
  position: 'relative',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  overflow: 'hidden',
}));

const MessageImgStyle = styled('img')(({ theme }) => ({
  height: 200,
  minWidth: 180,
  width: '100%',
  cursor: 'pointer',
  objectFit: 'cover',
  borderRadius: 12,
  marginBottom: 8,
}));

/** Preserve Quill/HTML line breaks; plain text newlines become <br>. */
function normalizeMessageHtml(text = '') {
  const raw = String(text || '')
  if (!raw.trim()) return ''
  if (!/<[a-z][\s\S]*>/i.test(raw)) {
    return raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>')
  }
  // Quill soft breaks (Shift+Enter) can leave literal \n inside <p> tags; browsers collapse those.
  return raw
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '<br>')
    .replace(/<p>(\s|&nbsp;)*<\/p>/gi, '<p><br></p>')
}

/** Styles for Quill HTML (lists/links) inside chat bubbles */
const richMessageSx = {
  wordWrap: 'break-word',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  textAlign: 'left',
  color: WA.text,
  maxWidth: '100%',
  '& p': {
    margin: 0,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& p + p': {
    marginTop: '0.35em',
  },
  '& a': {
    color: WA.send,
    textDecoration: 'underline',
    wordBreak: 'break-all',
  },
  '& ol, & ul': {
    margin: '0.25em 0',
    paddingLeft: '1.35em',
    listStylePosition: 'outside',
  },
  '& ol': {
    listStyleType: 'decimal',
  },
  '& ul': {
    listStyleType: 'disc',
  },
  '& li': {
    margin: '0.15em 0',
    paddingLeft: '0.15em',
    display: 'list-item',
    listStyle: 'inherit',
  },
  // Quill sometimes uses <ol> for both ordered + bullet via data-list
  '& li[data-list="ordered"]': {
    listStyleType: 'decimal',
  },
  '& li[data-list="bullet"]': {
    listStyleType: 'disc',
  },
}

function AttachmentMaximizeButton({ onClick }) {
  return (
    <IconButton
      size="small"
      aria-label="Maximize attachment"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        if (onClick) onClick(event)
      }}
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 2,
        width: 32,
        height: 32,
        color: '#fff',
        bgcolor: 'rgba(0,0,0,0.55)',
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.15s ease',
        '.attachment-preview:hover &': {
          opacity: 1,
          pointerEvents: 'auto',
        },
        '&:hover': {
          bgcolor: 'rgba(0,0,0,0.75)',
        },
      }}
    >
      <FullscreenIcon sx={{ fontSize: 18 }} />
    </IconButton>
  )
}

function PendingJoinInvite({
  invitedName,
  avatar,
  timeLabel,
  onJoin,
  joining = false,
  viewerIsInvited = false,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        my: 2.5,
        px: 2,
      }}
    >
      <Avatar
        alt={invitedName}
        src={avatar || defaultAvatar}
        sx={{ width: 44, height: 44, mb: 1, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)' }}
      />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: WA.text, mb: 0.5 }}>
        {viewerIsInvited
          ? 'Help Desk invited you to this chat'
          : `${invitedName} requested to chat`}
      </Typography>
      {timeLabel ? (
        <Typography variant="caption" sx={{ color: WA.meta, mb: viewerIsInvited ? 1.25 : 0 }}>
          {timeLabel}
        </Typography>
      ) : null}
      {viewerIsInvited && typeof onJoin === 'function' ? (
        <Button
          variant="contained"
          onClick={onJoin}
          disabled={joining}
          sx={{
            mt: 0.5,
            bgcolor: WA.header,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            borderRadius: 24,
            boxShadow: '0 8px 18px rgba(2, 125, 194, 0.25)',
            '&:hover': { bgcolor: WA.headerDark },
          }}
        >
          {joining ? 'Joining...' : 'Join Chat'}
        </Button>
      ) : null}
    </Box>
  )
}

function JoinEvent({ name, avatar, timeLabel, action = 'joined the chat' }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        my: 2.5,
        px: 2,
      }}
    >
      <Avatar
        alt={name}
        src={avatar || defaultAvatar}
        sx={{ width: 44, height: 44, mb: 1, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)' }}
      />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: WA.text }}>
        {action ? `${name} ${action}` : name}
      </Typography>
      {timeLabel ? (
        <Typography variant="caption" sx={{ color: WA.meta, mt: 0.25 }}>
          {timeLabel}
        </Typography>
      ) : null}
    </Box>
  )
}

function ChatEvent({ text, timeLabel }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        my: 1.5,
        px: 2,
      }}
    >
      <Typography variant="caption" sx={{ color: WA.meta, fontWeight: 600 }}>
        {text}
      </Typography>
      {timeLabel ? (
        <Typography variant="caption" sx={{ color: WA.meta, mt: 0.25 }}>
          {timeLabel}
        </Typography>
      ) : null}
    </Box>
  )
}

function EndEvent({ timeLabel, endedByYou, rating = null, showRating = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        my: 2.5,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: '#EEF2F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1,
          border: `1px solid ${WA.border}`,
        }}
      >
        <Typography sx={{ fontWeight: 800, color: WA.meta, fontSize: 18 }}>×</Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: WA.text }}>
        {endedByYou ? 'You ended the chat' : 'Chat ended'}
      </Typography>
      {timeLabel ? (
        <Typography variant="caption" sx={{ color: WA.meta, mt: 0.25 }}>
          {timeLabel}
        </Typography>
      ) : null}
      {showRating ? (
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Typography variant="caption" sx={{ color: WA.meta, fontWeight: 600 }}>
            Customer feedback
          </Typography>
          {rating ? (
            rating === 'skip' || rating === 'skipped' ? (
              <Typography variant="caption" sx={{ color: WA.meta }}>
                Review skipped by user
              </Typography>
            ) : (
              <ChatRatingBadge rating={rating} size="medium" />
            )
          ) : (
            <Typography variant="caption" sx={{ color: WA.meta }}>
              Waiting for customer rating
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  )
}

export default function ChatMessageItemSignWise({
  message,
  onOpenLightbox,
  onOpenAttachmentPreview,
  participants = [],
  recipientOnline = false,
  chatRating = null,
  showEndRating = false,
  onReply = null,
  onJoinChat = null,
  joiningChat = false,
  isGroupChat = false,
  ...other
}) {
  const isSystem = message.contentType === 'system' || message.message_type === 'system';
  const isEnd = message.contentType === 'end' || message.message_type === 'end';
  const isBot = message.contentType === 'bot';
  const isMe = message.sender === 'You';
  const timeLabel =
    message?.created_at && moment.unix(message.created_at).isValid()
      ? moment.unix(message.created_at).format('h:mm A')
      : '';
  const rawHtml = normalizeMessageHtml(message.text || message.message || '');
  const { reply: quotedReply, bodyHtml } = splitReplyHtml(rawHtml)
  const html = bodyHtml;
  const joinName = 'Help Desk';
  const eventTime =
    message?.created_at && moment.unix(message.created_at).isValid()
      ? moment.unix(message.created_at).format('D MMM YYYY, h:mm A')
      : timeLabel;

  if (isEnd) {
    return (
      <EndEvent
        endedByYou={isMe}
        timeLabel={eventTime}
        showRating={showEndRating}
        rating={chatRating?.rating || chatRating || null}
      />
    )
  }

  if (isSystem) {
    const systemText = html.replace(/<(.|\n)*?>/g, ' ').replace(/&nbsp;/g, ' ').trim()
    const pendingJoin = parsePendingJoinMessage(systemText)
    if (pendingJoin) {
      const viewer = getLoggedInUser()
      const viewerId = Number(viewer?.user_id)
      const isHelpDesk = viewerId === HELP_DESK_USER_ID
      const isInvited = viewerId === Number(pendingJoin.userId)
      const invitedMembership = participants.find(
        (person) => Number(person.user_id) === Number(pendingJoin.userId),
      )
      if (Number(invitedMembership?.join_status) === 1) return null
      if (!isHelpDesk && !isInvited) return null
      return (
        <PendingJoinInvite
          invitedName={pendingJoin.name}
          avatar={personPhoto(pendingJoin.name, participants, message.profile_pic)}
          timeLabel={eventTime}
          onJoin={onJoinChat}
          joining={joiningChat}
          viewerIsInvited={isInvited}
        />
      )
    }
    const addedName = systemText.match(/^(.*) was added to the chat$/i)?.[1]?.trim()
    const leftName = systemText.match(/^(.*) left the chat$/i)?.[1]?.trim()
    if (!systemText || systemText === START_CHAT_MESSAGE) {
      return (
        <JoinEvent
          name={joinName}
          avatar={message.profile_pic}
          timeLabel={eventTime}
        />
      )
    }
    if (addedName) {
      if (isAddedUserViewer(addedName, participants)) {
        const user = getLoggedInUser()
        return (
          <JoinEvent
            name="You"
            avatar={
              personPhoto(addedName, participants, message.profile_pic) ||
              isPhotoUrl(user?.profile_pic) ||
              defaultAvatar
            }
            timeLabel={eventTime}
          />
        )
      }
      return (
        <JoinEvent
          name={addedName}
          avatar={personPhoto(addedName, participants, message.profile_pic)}
          timeLabel={eventTime}
        />
      )
    }
    if (leftName) {
      return (
        <JoinEvent
          name={leftName}
          avatar={personPhoto(leftName, participants, message.profile_pic)}
          timeLabel={eventTime}
          action="left the chat"
        />
      )
    }
    return <ChatEvent text={systemText} timeLabel={eventTime} />
  }

  const plainText = html.replace(/<(.|\n)*?>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  const attachmentName = (() => {
    if (!message.attachment) return 'Document'
    try {
      const path = String(message.attachment).split('?')[0]
      return cleanAttachmentName(path.split('/').pop() || 'Document')
    } catch {
      return 'Document'
    }
  })()
  const displayFileName = attachmentName
  const showFileCaption =
    Boolean(plainText) &&
    cleanAttachmentName(plainText).toLowerCase() !==
      cleanAttachmentName(attachmentName).toLowerCase()
  const fileMeta = getFileMeta(displayFileName)
  const FileTypeIcon = fileMeta.Icon
  const attachmentKind =
    message.contentType === 'video'
      ? 'video'
      : message.contentType === 'audio'
      ? 'audio'
      : getAttachmentKind(message.attachment, displayFileName)
  const isImageMsg =
    message.contentType === 'image' ||
    attachmentKind === 'image' ||
    (message.attachment &&
      /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(message.attachment)))
  const isFileMsg =
    Boolean(message.attachment) &&
    !isImageMsg &&
    (message.contentType === 'file' ||
      message.contentType === 'video' ||
      message.contentType === 'audio' ||
      (message.contentType !== 'gif' && attachmentKind !== 'image'))

  const openAttachmentPreview = () => {
    if (typeof onOpenAttachmentPreview === 'function') {
      onOpenAttachmentPreview({
        url: message.attachment,
        name: displayFileName,
        kind: attachmentKind,
      })
      return
    }
    if (message.attachment) {
      window.open(message.attachment, '_blank', 'noopener,noreferrer')
    }
  }

  if (!message.isTyping && !isImageMsg && !isFileMsg && !plainText) {
    return null;
  }

  const receiptStatus = isMe ? getMessageReceiptStatus(message, recipientOnline, isGroupChat) : null
  const canReply =
    typeof onReply === 'function' &&
    !message.isTyping &&
    !isBot &&
    !isSystem &&
    !isEnd

  const quoteBlock = quotedReply ? (
    <Box
      role={quotedReply.id ? 'button' : undefined}
      tabIndex={quotedReply.id ? 0 : undefined}
      onClick={(event) => {
        event.stopPropagation()
        const targetId = quotedReply.id
        if (!targetId) return
        const safeId = String(targetId).replace(/"/g, '')
        const el =
          document.getElementById(`chat-msg-${safeId}`) ||
          document.querySelector(`[data-message-id="${safeId}"]`)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('chat-msg-highlight')
        window.setTimeout(() => el.classList.remove('chat-msg-highlight'), 1600)
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        event.currentTarget.click()
      }}
      sx={{
        mb: 0.75,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        borderLeft: `3px solid ${WA.header}`,
        bgcolor: isMe ? 'rgba(255,255,255,0.72)' : 'rgba(2, 125, 194, 0.08)',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        cursor: quotedReply.id ? 'pointer' : 'default',
        '&:hover': quotedReply.id
          ? { bgcolor: isMe ? 'rgba(255,255,255,0.9)' : 'rgba(2, 125, 194, 0.12)' }
          : undefined,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          color: '#212B36',
          lineHeight: 1.2,
        }}
      >
        {quotedReply.sender}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#212B36',
          lineHeight: 1.3,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
        title={quotedReply.preview}
      >
        {quotedReply.preview}
      </Typography>
    </Box>
  ) : null

  return (
    <RootStyle
      id={message?.id != null ? `chat-msg-${message.id}` : undefined}
      data-message-id={message?.id != null ? String(message.id) : undefined}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          maxWidth: '80%',
          minWidth: 0,
          ...(isMe && { ml: 'auto' }),
          '&:hover .chat-reply-btn': { opacity: 1 },
        }}
      >
        {!isMe && (
          <Avatar
            alt={message.sender}
            src={message.profile_pic || undefined}
            sx={{ width: 32, height: 32, mr: 1 }}
          />
        )}
        {canReply && isMe ? (
          <IconButton
            className="chat-reply-btn"
            size="small"
            aria-label="Reply"
            onClick={() => onReply(message)}
            sx={{
              opacity: 0,
              mr: 0.5,
              mb: 0.5,
              color: WA.meta,
              width: 28,
              height: 28,
              transition: 'opacity 0.15s',
              '&:hover': { bgcolor: 'rgba(2, 125, 194, 0.08)', color: WA.header },
            }}
          >
            <ReplyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        <ContentStyle
          className="chat-bubble-content"
          sx={{
            backgroundColor: isMe ? WA.outgoing : isBot ? '#EAF6FD' : WA.incoming,
            border: isMe ? 'none' : `1px solid ${WA.border}`,
            borderRadius: isMe ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
            boxShadow: isMe ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.04)',
          }}
        >
          {quoteBlock}
          {isImageMsg ? (
            <>
              <Box
                className="attachment-preview"
                sx={{
                  position: 'relative',
                  mb: plainText ? 1 : 0,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}
              >
                <MessageImgStyle
                  alt="attachment"
                  src={message.attachment}
                  onClick={() => onOpenLightbox(message.attachment)}
                  sx={{ marginBottom: 0 }}
                />
                <AttachmentMaximizeButton
                  onClick={() => onOpenLightbox(message.attachment)}
                />
              </Box>
              {plainText ? (
                <Typography
                  component="div"
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: html }}
                  sx={richMessageSx}
                />
              ) : null}
            </>
          ) : isFileMsg ? (
            <>
              {attachmentKind === 'video' ? (
                <Box
                  className="attachment-preview"
                  sx={{
                    position: 'relative',
                    mb: showFileCaption ? 1 : 0,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="video"
                    src={message.attachment}
                    controls
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxHeight: 240,
                      bgcolor: '#000',
                      cursor: 'pointer',
                    }}
                  />
                  <AttachmentMaximizeButton onClick={openAttachmentPreview} />
                </Box>
              ) : attachmentKind === 'audio' ? (
                <Box
                  component="audio"
                  src={message.attachment}
                  controls
                  onClick={(event) => event.stopPropagation()}
                  sx={{ display: 'block', width: '100%', mb: showFileCaption ? 1 : 0 }}
                />
              ) : canShowPagePreview(attachmentKind) ? (
                <Box
                  className="attachment-preview"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    openAttachmentPreview()
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      openAttachmentPreview()
                    }
                  }}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 220,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: `1px solid ${WA.border}`,
                    mb: showFileCaption ? 1 : 0,
                    cursor: 'pointer',
                    bgcolor: '#F4F6F8',
                  }}
                >
                  {isSpreadsheetKind(attachmentKind, displayFileName) ||
                  isSpreadsheetKind(attachmentKind, message.attachment) ? (
                    <SpreadsheetPagePreview
                      url={message.attachment}
                      fileName={displayFileName}
                    />
                  ) : (
                    <Box
                      component="iframe"
                      title={displayFileName}
                      src={getInlinePreviewSrc(attachmentKind, message.attachment)}
                      sx={{
                        border: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        bgcolor: '#fff',
                      }}
                    />
                  )}
                  <AttachmentMaximizeButton onClick={openAttachmentPreview} />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      px: 1.25,
                      py: 0.75,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FileTypeIcon sx={{ fontSize: 18 }} />
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ flexGrow: 1, fontWeight: 600 }}
                      title={displayFileName}
                    >
                      {displayFileName}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  className="attachment-preview"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    openAttachmentPreview()
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      openAttachmentPreview()
                    }
                  }}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    textDecoration: 'none',
                    color: 'inherit',
                    mb: showFileCaption ? 1 : 0,
                    px: 1,
                    py: 1,
                    borderRadius: 1.5,
                    bgcolor: isMe ? 'rgba(2, 125, 194, 0.08)' : 'rgba(0,0,0,0.04)',
                    minWidth: 220,
                    maxWidth: 280,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isMe ? 'rgba(2, 125, 194, 0.14)' : 'rgba(0,0,0,0.06)',
                    },
                  }}
                >
                  <AttachmentMaximizeButton onClick={openAttachmentPreview} />
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.25,
                      bgcolor: fileMeta.color,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      position: 'relative',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    }}
                  >
                    <FileTypeIcon sx={{ fontSize: 26 }} />
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -4,
                        bottom: -4,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: '#fff',
                        border: `1px solid ${WA.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <GetAppIcon sx={{ fontSize: 12, color: WA.meta }} />
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: WA.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                      }}
                      title={displayFileName}
                    >
                      {displayFileName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: WA.meta, display: 'block', mt: 0.25, lineHeight: 1.2 }}
                    >
                      {fileMeta.label} · Preview
                    </Typography>
                  </Box>
                </Box>
              )}
              {showFileCaption ? (
                <Typography
                  component="div"
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: html }}
                  sx={richMessageSx}
                />
              ) : null}
            </>
          ) : message.isTyping ? (
            <TypingAnimation />
          ) : (
            <Typography
              component="div"
              variant="body2"
              dangerouslySetInnerHTML={{ __html: html }}
              sx={richMessageSx}
            />
          )}
          {timeLabel && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.25,
                mt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: WA.text,
                  fontSize: 11,
                  lineHeight: 1,
                }}
              >
                {timeLabel}
              </Typography>
              {receiptStatus ? (
                <MessageTicks status={receiptStatus} />
              ) : null}
            </Box>
          )}
        </ContentStyle>
        {canReply && !isMe ? (
          <IconButton
            className="chat-reply-btn"
            size="small"
            aria-label="Reply"
            onClick={() => onReply(message)}
            sx={{
              opacity: 0,
              ml: 0.5,
              mb: 0.5,
              color: WA.meta,
              width: 28,
              height: 28,
              transition: 'opacity 0.15s',
              '&:hover': { bgcolor: 'rgba(2, 125, 194, 0.08)', color: WA.header },
            }}
          >
            <ReplyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Box>
    </RootStyle>
  );
}

ChatMessageItemSignWise.propTypes = {
  message: PropTypes.object.isRequired,
  onOpenLightbox: PropTypes.func,
  onOpenAttachmentPreview: PropTypes.func,
};
